import React, { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import DirectoryPage from './DirectoryPage';
import { useTranslation } from '../i18n';

/**
 * SEO-friendly directory wrapper.
 * Routes like /directory/electronics or /directory/manufacturers/united-states
 * pre-set the filters and render the standard DirectoryPage.
 */

const INDUSTRY_MAP = {
  'manufacturing': 'manufacturing',
  'import-export': 'import_export',
  'wholesale': 'wholesale',
  'retail': 'retail',
  'technology': 'technology',
  'services': 'services',
  'construction': 'construction',
  'automotive': 'automotive',
  'food-beverage': 'food_beverage',
  'healthcare': 'healthcare',
  'electronics': 'electronics',
  'textiles': 'textiles',
  'chemicals': 'chemicals',
  'packaging': 'packaging'
};

const COMPANY_TYPE_MAP = {
  'manufacturers': 'manufacturer',
  'distributors': 'distributor',
  'wholesalers': 'wholesaler',
  'service-providers': 'service_provider',
  'trading-companies': 'trading_company'
};

const COUNTRY_MAP = {
  'united-states': 'United States',
  'china': 'China',
  'germany': 'Germany',
  'india': 'India',
  'japan': 'Japan',
  'united-kingdom': 'United Kingdom',
  'italy': 'Italy',
  'mexico': 'Mexico',
  'brazil': 'Brazil',
  'vietnam': 'Vietnam',
  'south-korea': 'South Korea',
  'turkey': 'Turkey',
  'indonesia': 'Indonesia',
  'thailand': 'Thailand',
  'canada': 'Canada',
  'france': 'France',
  'spain': 'Spain',
  'netherlands': 'Netherlands',
  'australia': 'Australia',
  'sweden': 'Sweden'
};

export default function SEODirectoryPage() {
  const { t } = useTranslation();
  const { slug: category, country } = useParams();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    // Build query params from URL segments
    const params = new URLSearchParams();

    if (category) {
      if (INDUSTRY_MAP[category]) {
        params.set('industry', INDUSTRY_MAP[category]);
      } else if (COMPANY_TYPE_MAP[category]) {
        params.set('company_type', COMPANY_TYPE_MAP[category]);
      }
    }

    if (country && COUNTRY_MAP[country]) {
      params.set('country', COUNTRY_MAP[country]);
    }

    // Update page title for SEO
    const industryLabel = category ? category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';
    const countryLabel = country ? COUNTRY_MAP[country] || country.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

    if (industryLabel && countryLabel) {
      document.title = `Find ${industryLabel} Suppliers in ${countryLabel} | PocketFiler`;
    } else if (industryLabel) {
      document.title = `Find ${industryLabel} Suppliers | PocketFiler`;
    }

    // Set meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      const desc = countryLabel
        ? `Browse verified ${industryLabel.toLowerCase()} suppliers in ${countryLabel} on PocketFiler. Get quotes, draft AI contracts, close deals in 21 languages.`
        : `Browse verified ${industryLabel.toLowerCase()} suppliers on PocketFiler. Get quotes, draft AI contracts, close deals in 21 languages.`;
      metaDesc.setAttribute('content', desc);
    }

    // Navigate to directory with filters
    if (params.toString()) {
      setSearchParams(params);
    }
  }, [category, country, setSearchParams]);

  // Render the standard DirectoryPage — filters will be picked up from URL params
  return <DirectoryPage />;
}
